/**
 * Auth validation schemas (Zod v4).
 * Shared between client forms and Edge Functions.
 *
 * Signup philosophy (A1 / Apple-level friction):
 * - Email path: email + password + terms only. Names/DOB/phone later.
 * - Phone path: phone + terms only. Names/DOB later (never re-ask if set).
 * - Security stays max: strong password rules, one verified contact to enter,
 *   full KYC/AML before booking/hosting.
 */
import { z } from 'zod'
import {
  parsePhoneNumberFromString,
  type PhoneNumber as ParsedPhoneNumber,
} from 'libphonenumber-js/max'
import { sanitizeName } from '../utils/sanitize'
import { AGE_REQUIREMENTS } from '../utils/constants'
import { yearsBetween } from '../utils/dates'
import { supportedPhoneCountries } from '../data/countries'

const MIN_SIGNUP_AGE = AGE_REQUIREMENTS.minimumBooking
const MAX_REASONABLE_AGE = 120

/**
 * The one phone rule in the app — now decided by arithmetic, not by a table we
 * maintain by hand.
 *
 * This used to be `SUBSCRIBER_DIGITS`, a per-country list of acceptable
 * subscriber lengths, and a length is not a numbering plan. `+502 9876 5432` is
 * eight digits and Guatemala has no such subscriber range; the table said yes,
 * the provider said no, and the person was charged for the difference in
 * confusion while we were charged for it in credits. `libphonenumber-js/max`
 * carries Google's actual per-country patterns AND the line type, so both
 * questions below are answered from the real plan:
 *
 *   · is this a number that can exist?        → isValidPhoneNumber
 *   · can a handset receive a message on it?  → getType() says MOBILE
 *
 * The `max` metadata subset is the one that knows line types; `min` does not,
 * and without types a Salvadoran landline (2xxx-xxxx) passes as sendable. That
 * is the whole reason for the extra ~74 KB over `min`.
 *
 * The COUNTRY allow-list stays ours: see SUPPORTED_PHONE_COUNTRY_ISO2S in
 * lib/data/countries.ts. libphonenumber says whether a number is real; it has
 * no opinion on which markets we have chosen to verify. Both facts are required
 * and they are separate.
 */
const SUPPORTED_ISO2S: ReadonlySet<string> = new Set<string>(
  supportedPhoneCountries().map((country) => country.iso2),
)

/**
 * Line types that can receive a WhatsApp OTP.
 *
 * FIXED_LINE_OR_MOBILE is included because the NANP (US/CA) and Mexico cannot
 * distinguish the two from the number alone — libphonenumber reports the
 * ambiguity honestly rather than guessing. Refusing it would refuse every US,
 * Canadian and Mexican mobile. Everywhere the plan DOES separate them (SV, GT,
 * HN, NI, CR, PA, BZ), a landline is reported as FIXED_LINE and is refused
 * here, which is what the hand-written `+503` 6-or-7 rule used to do for one
 * country only.
 */
const OTP_CAPABLE_LINE_TYPES: ReadonlySet<string> = new Set<string>([
  'MOBILE',
  'FIXED_LINE_OR_MOBILE',
])

/**
 * A real number, in a country we verify.
 *
 * Deliberately does NOT consider the line type — `verify-phone.tsx` uses this
 * to tell "that is not a number" apart from "that number cannot receive the
 * code", and collapsing the two produces the message that helps nobody.
 */
export function isSupportedPhone(value: string): boolean {
  const parsed = parsePhoneNumber(value.trim())
  if (!parsed?.isValid()) return false
  return parsed.country !== undefined && SUPPORTED_ISO2S.has(parsed.country)
}

/**
 * The number is real, in a supported country, AND on a line a handset answers.
 *
 * This is THE gate. Nothing may leave the device for a number this refuses —
 * every refusal here is a Verifik credit not spent on a message that could
 * never arrive. See lib/utils/otp-delivery.ts, which is what callers use.
 */
export function isOtpCapablePhone(value: string): boolean {
  const parsed = parsePhoneNumber(value.trim())
  if (!parsed?.isValid()) return false
  if (parsed.country === undefined || !SUPPORTED_ISO2S.has(parsed.country)) return false
  const lineType = parsed.getType()
  return lineType !== undefined && OTP_CAPABLE_LINE_TYPES.has(lineType)
}

/**
 * The number exists under SOME country's plan — nothing about which country, or
 * whether a handset answers it.
 *
 * This is the "is that even a phone number" half of the decision, kept separate
 * so the field can tell someone mid-type ("keep going") apart from someone
 * whose number is real but out of our markets ("we cannot verify Spain yet").
 * One boolean cannot say both, and saying the wrong one is how a correct number
 * gets read as a typo.
 */
export function isPlausiblePhoneNumber(value: string): boolean {
  return parsePhoneNumber(value.trim())?.isValid() === true
}

/**
 * Parse without throwing. `parsePhoneNumber` from the `/max` subset throws
 * `ParseError` on input it cannot even tokenise ('', '+', 'abc'), and the phone
 * field hands us exactly that on every keystroke while someone is still typing.
 */
function parsePhoneNumber(value: string): ParsedPhoneNumber | undefined {
  if (!value.startsWith('+')) return undefined
  try {
    return parsePhoneNumberFromString(value)
  } catch {
    return undefined
  }
}

// Shared password schema — single source of truth for register + reset
const passwordSchema = z
  .string()
  .min(8, 'auth.validation.password_min')
  .max(128, 'auth.validation.password_max')
  .regex(/[a-z]/, 'auth.validation.password_lowercase')
  .regex(/[A-Z]/, 'auth.validation.password_uppercase')
  .regex(/\d/, 'auth.validation.password_number')

/**
 * Zero-friction email signup: identity of the account is email + password.
 * Profile fields (name, DOB, phone) are progressive — collected once later
 * where the product actually needs them (booking / verification hub).
 */
export const registerSchema = z.object({
  email: z
    .string()
    .email('auth.validation.email_invalid')
    .transform((v) => v.toLowerCase().trim()),
  password: passwordSchema,
  acceptTerms: z.literal(true, { error: 'auth.validation.accept_terms_required' }),
  /** UI locale at signup — progressive profile, never re-asked. */
  preferredLocale: z.enum(['es', 'en']).optional().default('es'),
})

export const loginSchema = z.object({
  email: z
    .string()
    .email('auth.validation.email_invalid')
    .transform((v) => v.toLowerCase().trim()),
  password: z.string().min(1, 'auth.validation.password_min'),
})

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .email('auth.validation.email_invalid')
    .transform((v) => v.toLowerCase().trim()),
})

export const otpSchema = z.object({
  code: z
    .string()
    .length(6)
    .regex(/^\d{6}$/, 'auth.errors.otp_invalid'),
})

/**
 * Optional progressive profile — used when we already have a session and
 * need name/DOB once. Empty strings become undefined; never re-require
 * fields that are already on the profile.
 */
export const progressiveProfileSchema = z.object({
  firstName: z
    .string()
    .optional()
    .transform((v) => {
      const t = (v ?? '').trim()
      return t.length === 0 ? undefined : sanitizeName(t)
    })
    .refine((v) => v === undefined || v.length >= 2, 'auth.validation.first_name_min'),
  lastName: z
    .string()
    .optional()
    .transform((v) => {
      const t = (v ?? '').trim()
      return t.length === 0 ? undefined : sanitizeName(t)
    })
    .refine((v) => v === undefined || v.length >= 2, 'auth.validation.last_name_min'),
  dateOfBirth: z
    .date({ error: 'auth.validation.dob_required' })
    .optional()
    .refine((d) => {
      if (!d) return true
      const age = yearsBetween(d, new Date())
      return age >= MIN_SIGNUP_AGE && age <= MAX_REASONABLE_AGE
    }, 'auth.validation.dob_too_young'),
})

/**
 * Phone signup: only the number is required at OTP time.
 * Terms are checked on the screen (tago_accepted_terms metadata).
 * Identity fields are progressive after verify.
 */
export const phoneSignupMetadataSchema = z.object({
  firstName: progressiveProfileSchema.shape.firstName,
  lastName: progressiveProfileSchema.shape.lastName,
  dateOfBirth: progressiveProfileSchema.shape.dateOfBirth,
})

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'auth.validation.passwords_mismatch',
    path: ['confirmPassword'],
  })

export type RegisterInput = z.infer<typeof registerSchema>
export type LoginInput = z.infer<typeof loginSchema>
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>
export type OtpInput = z.infer<typeof otpSchema>
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>
export type PhoneSignupMetadataInput = z.infer<typeof phoneSignupMetadataSchema>
export type ProgressiveProfileInput = z.infer<typeof progressiveProfileSchema>
